import { APIClient } from "../helpers/api_helper";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../types/api";

const api = new APIClient();

const RESOURCE = "/orders";

export interface OrderClient {
  id: number;
  name: string;
}

export interface OrderUnit {
  id: number;
  name: string;
}

export interface OrderCatalogService {
  id: number;
  name: string;
  price: number;
  price_is_from?: boolean;
}

export interface OrderCatalogProduct {
  id: number;
  name: string;
  sale_price: number | null;
}

export interface OrderProfessional {
  id: number;
  name: string;
  commission_rate_bps?: number | null;
}

export interface OrderPixPayload {
  qr_code_base64: string;
  copy_paste_string: string;
}

export type OrderPaymentMethod =
  | "cash"
  | "pix_external"
  | "debit_card_terminal"
  | "credit_card_terminal"
  | "pix_gateway"
  | "card_terminal_gateway";

export type TerminalCardType = "debit" | "credit";

export { CHECKOUT_PAYMENT_METHOD_KEYS as ORDER_PAYMENT_METHOD_OPTIONS } from "./paymentMethodSettingsService";

export interface OrderPayment {
  id: number;
  payment_method: OrderPaymentMethod;
  amount_cents: number;
  fee_cents: number | null;
  gateway_slug: string | null;
  external_transaction_id: string | null;
  paid_at: string | null;
  voided_at?: string | null;
}

export interface OrderTerminalPayload {
  status: "awaiting_pos" | string;
  card_type: TerminalCardType | string | null;
  external_order_id: string | null;
  external_charge_id?: string | null;
  device_serial_number?: string | null;
}

export interface OrderCatalogServicePackage {
  id: number;
  name: string;
  sessions_count: number;
  price: number;
  session_unit_price: number;
}

export interface OrderItem {
  id: number;
  tenant_id: number;
  order_id: number;
  item_type: "service" | "retail" | "package";
  service_id: number | null;
  product_id: number | null;
  service_package_id?: number | null;
  professional_id: number | null;
  quantity: number;
  unit_price: number;
  paid_via_credit?: boolean;
  paid_via_loyalty?: boolean;
  paid_via_subscription?: boolean;
  credit_source?: "package" | "loyalty" | string | null;
  credit_opt_out?: boolean;
  client_credit_id?: number | null;
  client_subscription_id?: number | null;
  commission_amount: number | null;
  /** Rate frozen at checkout (bps). Prefer over professional.commission_rate_bps. */
  commission_rate_bps?: number | null;
  /** Current matrix rate for open lines (override → default). */
  effective_commission_rate_bps?: number | null;
  /** Rate that applied when the order was opened (open lines only, when matrix changed). */
  commission_rate_at_open_bps?: number | null;
  /** True when the matrix changed after this order was opened. */
  commission_rate_changed_since_open?: boolean;
  execution_notes: string | null;
  service?: OrderCatalogService | null;
  product?: OrderCatalogProduct | null;
  service_package?: OrderCatalogServicePackage | null;
  professional?: OrderProfessional | null;
  created_at: string;
  updated_at: string;
}

export interface OrderDiscount {
  id: number;
  discount_type: string;
  discount_percent_bps: number | null;
  discount_amount_cents: number;
  reason: string | null;
  applied_at: string | null;
  applied_by?: {
    id: number;
    name: string;
  } | null;
}

export interface Order {
  id: number;
  tenant_id: number;
  unit_id: number;
  client_id: number;
  appointment_id: number | null;
  subtotal_amount?: number;
  discount_total_cents?: number;
  total_amount: number;
  status: string;
  stock_debited_at: string | null;
  paid_at: string | null;
  voided_at?: string | null;
  voided_by_user_id?: number | null;
  void_reason?: string | null;
  client?: OrderClient;
  unit?: OrderUnit;
  order_items?: OrderItem[];
  payments?: OrderPayment[];
  discounts?: OrderDiscount[];
  pix?: OrderPixPayload;
  terminal?: OrderTerminalPayload;
  /** Present on GET /orders/{id} when clinical scope applies. */
  needs_health_record?: boolean;
  health_prompt_service_id?: number | null;
  created_at: string;
  updated_at: string;
}

export interface CreateOrderPayload {
  unit_id: number;
  client_id: number;
  appointment_id?: number | null;
}

export interface AddOrderItemPayload {
  item_type: "service" | "retail" | "package";
  service_id?: number | null;
  product_id?: number | null;
  service_package_id?: number | null;
  professional_id?: number | null;
  quantity?: number;
  execution_notes?: string | null;
  /** When false, skip package credit for this service line (persisted as credit_opt_out). */
  use_credit?: boolean;
}

export interface UpdateOrderItemPayload {
  execution_notes?: string | null;
  quantity?: number;
  unit_price?: number;
}

export interface CheckoutConsumptionItem {
  product_id: number;
  quantity_consumed: number;
}

export interface CheckoutPaymentLine {
  payment_method: OrderPaymentMethod;
  amount_cents: number;
  terminal_card_type?: TerminalCardType;
}

export interface CheckoutOrderPayload {
  payment_method?: OrderPaymentMethod;
  payments?: CheckoutPaymentLine[];
  terminal_card_type?: TerminalCardType;
  consumptions?: CheckoutConsumptionItem[];
}

export interface VoidOrderPayload {
  reason: string;
}

export interface OrderVoidPreview {
  order_id: number;
  total_amount: number;
  commission_total_cents: number;
  commission_lines: number;
  payments_count: number;
  payments_sum_cents: number;
  retail_stock: Array<{
    product_id: number;
    product_name: string;
    quantity: number;
  }>;
  has_recipe_or_consumption_stock: boolean;
  credit_debit_lines: number;
  package_grant_lots: number;
  will_attempt_gateway_refund?: boolean;
  gateway_refund_amount_cents?: number;
  gateway_refund_payments_count?: number;
  notes: string[];
}

export interface OrderDiscountPreview {
  subtotal_amount: number;
  discount_total_cents: number;
  total_amount: number;
}

export interface ApplyOrderDiscountPayload {
  discount_percent_bps?: number | null;
  discount_amount_cents?: number | null;
  reason?: string | null;
}

export interface OrderListParams extends PaginationParams {
  unit_id?: number;
  client_id?: number;
  status?: string;
  payment_method?: OrderPaymentMethod | string;
  has_discount?: boolean | 0 | 1;
  applied_by_user_id?: number;
  sort?: "id_desc" | "discount_desc" | "discount_asc";
}

export const getAll = (
  params?: OrderListParams
): Promise<PaginatedApiResponse<Order>> =>
  api.get<PaginatedApiResponse<Order>>(RESOURCE, params);

export const getById = (id: number): Promise<ApiResponse<Order>> =>
  api.get<ApiResponse<Order>>(`${RESOURCE}/${id}`);

export const create = (
  payload: CreateOrderPayload
): Promise<ApiResponse<Order>> =>
  api.create<ApiResponse<Order>>(RESOURCE, payload);

export const addItem = (
  orderId: number,
  payload: AddOrderItemPayload
): Promise<ApiResponse<OrderItem>> =>
  api.create<ApiResponse<OrderItem>>(`${RESOURCE}/${orderId}/items`, payload);

export const updateItem = (
  orderId: number,
  orderItemId: number,
  payload: UpdateOrderItemPayload
): Promise<ApiResponse<OrderItem>> =>
  api.update<ApiResponse<OrderItem>>(`${RESOURCE}/${orderId}/items/${orderItemId}`, payload);

export const removeItem = (
  orderId: number,
  orderItemId: number
): Promise<ApiResponse<Record<string, never>>> =>
  api.delete<ApiResponse<Record<string, never>>>(
    `${RESOURCE}/${orderId}/items/${orderItemId}`
  );

export const checkout = (
  orderId: number,
  payload: CheckoutOrderPayload
): Promise<ApiResponse<Order>> =>
  api.create<ApiResponse<Order>>(`${RESOURCE}/${orderId}/checkout`, payload);

export const cancelTerminalCharge = (
  orderId: number
): Promise<ApiResponse<Order>> =>
  api.create<ApiResponse<Order>>(
    `${RESOURCE}/${orderId}/terminal-charge/cancel`,
    {}
  );

export const voidOrder = (
  orderId: number,
  payload: VoidOrderPayload
): Promise<ApiResponse<Order>> =>
  api.create<ApiResponse<Order>>(`${RESOURCE}/${orderId}/void`, payload);

export const getVoidPreview = (
  orderId: number
): Promise<ApiResponse<OrderVoidPreview>> =>
  api.get<ApiResponse<OrderVoidPreview>>(`${RESOURCE}/${orderId}/void-preview`);

export const previewDiscount = (
  orderId: number,
  payload: ApplyOrderDiscountPayload
): Promise<ApiResponse<OrderDiscountPreview>> =>
  api.create<ApiResponse<OrderDiscountPreview>>(
    `${RESOURCE}/${orderId}/discounts/preview`,
    payload
  );

export const applyDiscount = (
  orderId: number,
  payload: ApplyOrderDiscountPayload
): Promise<ApiResponse<OrderDiscount>> =>
  api.create<ApiResponse<OrderDiscount>>(`${RESOURCE}/${orderId}/discounts`, payload);

export const removeDiscount = (
  orderId: number,
  discountId: number
): Promise<ApiResponse<Record<string, never>>> =>
  api.delete<ApiResponse<Record<string, never>>>(
    `${RESOURCE}/${orderId}/discounts/${discountId}`
  );

export const getCourtesyReasonSuggestions = (): Promise<
  ApiResponse<{ reasons: string[] }>
> =>
  api.get<ApiResponse<{ reasons: string[] }>>(
    `${RESOURCE}/courtesy-reason-suggestions`
  );
